import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useState } from "react"
import { InputOutlined } from "@nova/ds-component-input"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { FileExtension } from "@nova/constants"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import {
    useCreateUserCertificate,
    userCertificateS3Key,
    useUpdateUserCertificate,
} from "../user-certificates/UserCertificateQueries"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { CertificateQueryResponse } from "./PilotQueries"
import { AlertContainer } from "@nova/component-alert-container"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { Divider } from "components/Divider"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { Select } from "@nova/component-select"
import { Checkbox } from "@nova/component-checkbox"
import { ContactInputMultiSelect } from "themes/default-tailwind/buttons/ContactInput"
import { CertificateExpiryReminderConfig } from "@nova/schemas"

type ReminderState = {
    enabled: boolean
    leadDays: number
    notifyPilot: boolean
    notifyWorkspaceAdmins: boolean
    customEmails: string[]
}

const LEAD_DAY_OPTIONS = [
    { label: "30 days before", value: "30" },
    { label: "60 days before", value: "60" },
    { label: "90 days before", value: "90" },
    { label: "180 days before", value: "180" },
]

const defaultReminder = (
    config: CertificateExpiryReminderConfig | null | undefined
): ReminderState => ({
    enabled: config?.enabled ?? false,
    leadDays: config?.leadDays ?? 60,
    notifyPilot: config?.notifyPilot ?? true,
    notifyWorkspaceAdmins: config?.notifyWorkspaceAdmins ?? true,
    customEmails: config?.customEmails ?? [],
})

const toExpiryConfig = (
    reminder: ReminderState,
    expiresAt: string
): CertificateExpiryReminderConfig | null => {
    if (!reminder.enabled || !expiresAt) return null
    return {
        enabled: true,
        leadDays: reminder.leadDays,
        notifyPilot: reminder.notifyPilot,
        notifyWorkspaceAdmins: reminder.notifyWorkspaceAdmins,
        customEmails: reminder.customEmails,
    }
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const UploadPilotCertificationModal = ({
    onClose,
    onSubmit,
    userId,
}: {
    onClose: () => void
    onSubmit: () => void
    userId: string
}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { createUserCertificate, isCreating } = useCreateUserCertificate()

    const [name, setName] = useState("")
    const [certificateNumber, setCertificateNumber] = useState("")
    const [issuedAt, setIssuedAt] = useState<string>("")
    const [expiresAt, setExpiresAt] = useState<string>("")
    const [notes, setNotes] = useState("")
    const [file, setFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [reminder, setReminder] = useState<ReminderState>(defaultReminder(null))

    const hasInvalidCustomEmail = reminder.customEmails.some((e) => !isValidEmail(e))

    const handleSubmit = async () => {
        if (!orgId || !userId || !name) return

        setIsUploading(true)
        try {
            const cert = await createUserCertificate({
                name,
                certificate_number: certificateNumber || null,
                issued_at: issuedAt || null,
                expires_at: expiresAt || null,
                expiry_config: toExpiryConfig(reminder, expiresAt),
                notes: notes || null,
                file_name: file?.name ?? null,
                file_size_bytes: file?.size ?? null,
                user_id: userId,
            })

            if (!cert?.id) {
                throw new Error("Failed to create certificate record")
            }

            if (file) {
                await fileManagementApi.putFile(file, {
                    target: userCertificateS3Key(orgId, userId, cert.id),
                })
            }

            onSubmit()
        } catch (error) {
            console.error("Upload failed:", error)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-[480px] w-full"
            title="Add Certificate"
            closeModal={onClose}
            bottomBar={
                <div className="flex justify-end gap-2 w-full">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={onClose}
                        disabled={isCreating || isUploading}
                    />
                    <ButtonPrimary
                        title="Add Certificate"
                        onClick={handleSubmit}
                        disabled={isCreating || isUploading || !name || hasInvalidCustomEmail}
                    />
                </div>
            }
        >
            <PilotCertificationForm
                name={name}
                setName={setName}
                certificateNumber={certificateNumber}
                setCertificateNumber={setCertificateNumber}
                issuedAt={issuedAt}
                setIssuedAt={setIssuedAt}
                expiresAt={expiresAt}
                setExpiresAt={setExpiresAt}
                notes={notes}
                setNotes={setNotes}
                file={file}
                setFile={setFile}
                reminder={reminder}
                setReminder={setReminder}
                isSaving={isCreating}
                isUploading={isUploading}
            />
        </HighPriorityNovaModalContainer>
    )
}

export const EditPilotCertificationModal = ({
    onClose,
    onSubmit,
    certificate,
}: {
    onClose: () => void
    onSubmit: () => void
    certificate: CertificateQueryResponse[number]
}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { updateUserCertificate, isUpdating } = useUpdateUserCertificate()

    const [name, setName] = useState(certificate.name ?? "")
    const [certificateNumber, setCertificateNumber] = useState(certificate.certificate_number ?? "")
    const [issuedAt, setIssuedAt] = useState<string>(toDateInputValue(certificate.issued_at))
    const [expiresAt, setExpiresAt] = useState<string>(toDateInputValue(certificate.expires_at))
    const [notes, setNotes] = useState(certificate.notes ?? "")
    const [newFile, setNewFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [reminder, setReminder] = useState<ReminderState>(
        defaultReminder(certificate.expiry_config as CertificateExpiryReminderConfig | null)
    )
    const hasExistingFile = !!certificate.file_name
    const hasInvalidCustomEmail = reminder.customEmails.some((e) => !isValidEmail(e))

    const handleSubmit = async () => {
        if (!orgId || !name) return

        setIsUploading(true)
        try {
            await updateUserCertificate({
                certificateId: certificate.id,
                data: {
                    name,
                    certificate_number: certificateNumber || null,
                    issued_at: issuedAt || null,
                    expires_at: expiresAt || null,
                    expiry_config: toExpiryConfig(reminder, expiresAt),
                    notes: notes || null,
                    file_name: newFile ? newFile.name : (certificate.file_name ?? null),
                    file_size_bytes: newFile ? newFile.size : (certificate.file_size_bytes ?? null),
                },
            })

            if (newFile) {
                await fileManagementApi.putFile(newFile, {
                    target: userCertificateS3Key(orgId, certificate.user_id, certificate.id),
                })
            }

            onSubmit()
        } catch (error) {
            console.error("Edit failed:", error)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-[480px] w-full"
            title="Edit Certificate"
            closeModal={onClose}
            bottomBar={
                <div className="flex justify-end gap-2 w-full">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={onClose}
                        disabled={isUpdating || isUploading}
                    />
                    <ButtonPrimary
                        title="Edit Certificate"
                        onClick={handleSubmit}
                        disabled={isUpdating || isUploading || !name || hasInvalidCustomEmail}
                    />
                </div>
            }
        >
            <PilotCertificationForm
                name={name}
                setName={setName}
                certificateNumber={certificateNumber}
                setCertificateNumber={setCertificateNumber}
                issuedAt={issuedAt}
                setIssuedAt={setIssuedAt}
                expiresAt={expiresAt}
                setExpiresAt={setExpiresAt}
                notes={notes}
                setNotes={setNotes}
                file={newFile}
                setFile={setNewFile}
                reminder={reminder}
                setReminder={setReminder}
                isSaving={isUpdating}
                isUploading={isUploading}
            />
            {hasExistingFile && newFile && (
                <AlertContainer
                    type="warning"
                    title="Override file?"
                    body="Uploading a new file will override the existing file for this certification."
                    className="mt-2"
                />
            )}
        </HighPriorityNovaModalContainer>
    )
}

const toDateInputValue = (date: Date | null | undefined): string => {
    if (!date) return ""
    try {
        return date.toISOString().split("T")[0]
    } catch {
        return ""
    }
}

const PilotCertificationForm = ({
    name,
    setName,
    certificateNumber,
    setCertificateNumber,
    issuedAt,
    setIssuedAt,
    expiresAt,
    setExpiresAt,
    notes,
    setNotes,
    file,
    setFile,
    reminder,
    setReminder,
    isSaving,
    isUploading,
}: {
    name: string
    setName: (n: string) => void
    certificateNumber: string
    setCertificateNumber: (n: string) => void
    issuedAt: string
    setIssuedAt: (i: string) => void
    expiresAt: string
    setExpiresAt: (e: string) => void
    notes: string
    setNotes: (n: string) => void
    file: File | null
    setFile: (f: File) => void
    reminder: ReminderState
    setReminder: (r: ReminderState) => void
    isSaving: boolean
    isUploading: boolean
}) => {
    return (
        <div className="flex flex-col gap-4 pt-2">
            <InputOutlined
                label="Certificate Name*"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
            />
            <InputOutlined
                label="Certificate Number"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.currentTarget.value)}
            />

            <div className="flex items-center justify-between gap-2.5">
                <InputOutlined
                    type="date"
                    label="Issue Date"
                    value={issuedAt}
                    onChange={(e) => setIssuedAt(e.currentTarget.value)}
                />
                <InputOutlined
                    type="date"
                    label="Expiry Date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.currentTarget.value)}
                />
            </div>

            <Divider />

            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                    <h5 className="text-gray-100">Set expiry reminder</h5>
                    {!expiresAt && (
                        <small className="text-gray-400">
                            Set an expiry date to enable reminders
                        </small>
                    )}
                </div>
                <ToggleSwitch
                    isToggled={reminder.enabled}
                    setIsToggled={(v) => setReminder({ ...reminder, enabled: v })}
                    disabled={!expiresAt}
                />
            </div>

            {reminder.enabled && expiresAt && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <small className="text-gray-300">When</small>
                        <Select
                            options={LEAD_DAY_OPTIONS}
                            value={String(reminder.leadDays)}
                            onChange={(v) => setReminder({ ...reminder, leadDays: Number(v) })}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <small className="text-gray-300">Recipients</small>
                        <button
                            type="button"
                            className="flex items-center gap-3"
                            onClick={() =>
                                setReminder({ ...reminder, notifyPilot: !reminder.notifyPilot })
                            }
                        >
                            <Checkbox isChecked={reminder.notifyPilot} onClick={() => {}} />
                            <span className="text-gray-100 text-sm">Pilot</span>
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-3"
                            onClick={() =>
                                setReminder({
                                    ...reminder,
                                    notifyWorkspaceAdmins: !reminder.notifyWorkspaceAdmins,
                                })
                            }
                        >
                            <Checkbox
                                isChecked={reminder.notifyWorkspaceAdmins}
                                onClick={() => {}}
                            />
                            <span className="text-gray-100 text-sm">Workspace Admin</span>
                        </button>
                        <ContactInputMultiSelect
                            enabledFeatures={["email"]}
                            emails={reminder.customEmails}
                            setEmails={(v) => setReminder({ ...reminder, customEmails: v })}
                            placeholder="Add custom emails"
                        />
                    </div>
                </div>
            )}

            <Divider />

            <InputOutlined
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
            />

            <UploadDropBox
                allowedFileTypes={[
                    FileExtension.pdf,
                    FileExtension.png,
                    FileExtension.jpg,
                    FileExtension.jpeg,
                ]}
                onNewItems={async (files) => setFile(files[0])}
                isProcessing={isSaving || isUploading}
                title={file ? file.name : "Upload a File (optional)"}
                supportedMimeTypes={[]}
                maxUploads={1}
            />
        </div>
    )
}
